import {
  Fail,
  Left,
  Right,
  Success,
} from 'monet';

export function Questionnaire(
  patientId = null,
  questions = [],
  actions = [],
) {
  this.patientId = patientId;
  this.questions = questions;
  this.actions = actions;

  let start = JSON.stringify(this);
  this.refresh = () => {
    start = JSON.stringify(this);
  };
  this.isChanged = () => JSON.stringify(this) !== start;
}

/**
 * @description Вопрос анкеты
 *
 * @param id {Number|null}
 * @param groupId {string}
 * @param question {string}
 * @param isOpenQuestion {boolean}
 * @constructor
 */
export function PatientQuestion(
  id = null,
  groupId = '',
  question = '',
  isOpenQuestion = false,
) {
  this.id = id;
  this.groupId = groupId;
  this.question = question;
  this.isOpenQuestion = isOpenQuestion;
  this.toQuery = () => ({
    id: this.id,
    group_id: this.groupId,
    question: this.question,
    is_open_question: this.isOpenQuestion,
  });
}

/**
 * @description Запрос на редактирование адреса.
 * @param {questionnaire} questionnaire Анкета здоровья.
 * @param {number}  patientId Идентификатор пациента.
 * @constructor
 */
export function StoreQuestionnaireRequest(questions, patientId) {
  this.toQuery = () => ({
    questions,
  });
  this.getPatientId = () => patientId;
}

/**
 * @description Шлюз анкет здоровья.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function PatientHealthQuestionnaireGateway(axios) {
  /**
   * @description Получить анкету здоровья пациента.
   * @param {Number} patientId Идентификатор пациента
   * @returns {Promise}
   */
  this.getQuestionnaire = async (patientId) => {
    try {
      const { data } = await axios.get(`/api/patients/${patientId}/questionnaire`);
      const questionnaire = new Questionnaire(data.patient_id, data.questions, data.actions);
      return Right(questionnaire);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Left(error.response.data);
      }
      throw error;
    }
  };
  /**
   * Удалить вопрос
   *
   * @param {Number} patientId Идентификатор пациента
   * @param {Number} questionId Идентификатор вопроса
   *
   * @returns {Promise}
   */
  this.deleteQuestion = async (patientId, questionId) => {
    try {
      const { data } = await axios
        .delete(`/api/patients/${patientId}/questionnaire-question/${questionId}`);
      return Right(data);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Left(error.response.data);
      }
      throw error;
    }
  };
  /**
   * Создать вопрос
   *
   * @param {PatientQuestion} patientQuestion Вопрос
   *
   * @returns {Promise}
   */
  this.createQuestion = async (patientQuestion) => {
    try {
      const { data } = await axios
        .post('/api/patients/questionnaire-question', patientQuestion.toQuery());
      return Right(data);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Left(error.response.data);
      }
      throw error;
    }
  };
  /**
   * Изменить вопрос
   *
   * @param {PatientQuestion} patientQuestion Вопрос
   * @param {Number} patientId Идентификатор пациент
   *
   * @returns {Promise}
   */
  this.editQuestion = async (patientQuestion, patientId) => {
    try {
      const { data } = await axios
        .put(`/api/patients/${patientId}/questionnaire-question`, patientQuestion.toQuery());
      return Right(data);
    } catch (error) {
      if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
        return Left(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Сохранить анкету здоровья пациента.
   * @param {StoreQuestionnaireRequest} storeQuestionnaireRequest Запрос.
   * @returns {Promise}
   */
  this.storeQuestionnaire = async (request) => {
    try {
      const response = await axios.put(
        `/api/patients/${request.getPatientId()}/questionnaire`,
        request.toQuery(),
      );
      return Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Fail(error.response.data);
      }
      throw error;
    }
  };
}
